/**
 * API utilities for making authenticated requests to backend
 */
import { auth } from './firebase';
const API_BASE = process.env.REACT_APP_API_BASE || "https://mockbot-api1.onrender.com";

/**
 * Firebase restores a persisted session asynchronously — right after a page
 * load/refresh, auth.currentUser is briefly null even for an already-logged-in
 * user, until the SDK finishes rehydrating it. A data-fetch that fires in that
 * window (e.g. a component's mount-time useEffect) would otherwise send its
 * request with no Authorization header at all, which the backend correctly
 * rejects as 401 with uid=null — not an expired-token error, just "no token
 * was ever attached." Waiting once for the SDK's first auth-state callback
 * (cached so every later call resolves instantly) closes that window.
 */
let _authReadyPromise = null;
function waitForAuthReady() {
  if (!_authReadyPromise) {
    _authReadyPromise = new Promise((resolve) => {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
  return _authReadyPromise;
}

/**
 * Get the current user's ID token for authentication
 */
export async function getAuthToken() {
  const user = auth.currentUser || await waitForAuthReady();
  if (!user) {
    return null;
  }
  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
}

/**
 * Make an authenticated API request
 */
export async function authenticatedFetch(url, options = {}) {
  const token = await getAuthToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers
  });
}

/**
 * Register/update user in backend after Firebase authentication
 * CRITICAL: This must be called IMMEDIATELY after Firebase login
 * The backend response includes redirect_to which controls ALL routing
 */
export async function registerUser(user, provider = 'email', profile = {}) {
  try {
    if (!user) {
      console.error('❌ registerUser: No user provided');
      return null;
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ registerUser: Could not get Firebase ID token');
      return null;
    }

    console.log('📤 Calling POST /auth/register with token for:', user.email);

    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        firebase_uid: user.uid,
        email: user.email,
        display_name: profile.display_name || user.displayName || '',
        photo_url: user.photoURL || '',
        provider: provider,
        phone_number: profile.phone_number || '',
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Backend /auth/register failed: ${response.status} - ${errorText}`);
      return null;
    }
    
    const data = await response.json();
    console.log('✅ Backend /auth/register response:', data);
    
    // CRITICAL: Validate redirect_to field
    if (!data.redirect_to) {
      console.error('❌ SECURITY: Backend response missing redirect_to field');
      return null;
    }
    
    const validRedirects = ['PAYMENT', 'INTERVIEWEE_DASHBOARD', 'COMPANY_DASHBOARD', 'COLLEGE_DASHBOARD'];
    if (!validRedirects.includes(data.redirect_to)) {
      console.error('❌ SECURITY: Invalid redirect_to value from backend:', data.redirect_to);
      return null;
    }
    
    console.log('✅ Backend routing instruction is valid:', data.redirect_to);
    return data;
  } catch (error) {
    console.error('❌ Failed to register user:', error);
    return null;
  }
}

/**
 * Start a new interview session in the backend
 */
export async function startInterviewSession(interviewConfig) {
  try {
    const response = await authenticatedFetch(`${API_BASE}/session/start`, {
      method: 'POST',
      body: JSON.stringify(interviewConfig)
    });
    
    const data = await response.json();
    console.log('✅ Session started:', data);
    return data;
  } catch (error) {
    console.error('❌ Failed to start session:', error);
    return null;
  }
}

/**
 * Get user's interview session history
 */
export async function getUserSessions() {
  try {
    const response = await authenticatedFetch(`${API_BASE}/user/sessions`);
    const data = await response.json();
    return data.sessions || [];
  } catch (error) {
    console.error('❌ Failed to get sessions:', error);
    return [];
  }
}

/**
 * Get messages from a specific session
 */
export async function getSessionMessages(sessionId) {
  try {
    const response = await authenticatedFetch(
      `${API_BASE}/session/${sessionId}/messages`
    );
    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('❌ Failed to get messages:', error);
    return [];
  }
}

/**
 * Create a payment order for subscription or access code
 * CRITICAL: This is called from the payment/pricing page to activate access
 */
export async function createPaymentOrder(planType = null, discountCode = null, collegeCode = null, jobId = null) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ createPaymentOrder: No authenticated user');
      return null;
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ createPaymentOrder: Could not get Firebase ID token');
      return null;
    }

    // Build request body with only non-null values
    const requestBody = {};
    if (planType) requestBody.plan_type = planType;
    if (discountCode) requestBody.discount_code = discountCode;
    if (collegeCode) requestBody.college_code = collegeCode;
    if (jobId) requestBody.job_id = jobId;

    // Ensure at least one field is provided
    if (Object.keys(requestBody).length === 0) {
      console.error('❌ createPaymentOrder: At least one access method required');
      return null;
    }

    console.log('📤 Calling POST /payment/create-order with:', requestBody);

    const response = await fetch(`${API_BASE}/payment/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    console.log('Backend response:', data);

    if (!response.ok) {
      const errorMsg = data.message || data.error_code || 'Payment processing failed';
      console.error(`❌ Backend /payment/create-order failed: ${response.status} - ${errorMsg}`);
      return {
        status: 'error',
        message: errorMsg
      };
    }

    // Validate successful response
    if (data.status === 'success' && data.entitlement_id) {
      console.log('✅ Entitlement created:', data.entitlement_id);
      return {
        status: 'success',
        entitlement_id: data.entitlement_id,
        order_id: data.order_id || null,
        subscription_id: data.subscription_id || null,
        is_test_order: data.is_test_order || false,
        plan_type: data.plan_type
      };
    } else {
      console.error('❌ Unexpected response format:', data);
      return {
        status: 'error',
        message: 'Invalid response from server'
      };
    }
  } catch (error) {
    console.error('❌ Failed to create payment order:', error);
    return {
      status: 'error',
      message: error.message || 'Network error'
    };
  }
}

/**
 * Resolve user access via one of 4 methods (coupon, college, job, or plan)
 * Called from Payment page after user confirms their selection
 * 
 * CRITICAL: This endpoint is the source of truth for access resolution
 * Backend determines redirect_to value based on what was resolved
 * 
 * @param {Object} payload - Request payload with ONE of:
 *   { coupon_code: "value" }
 *   { college_code: "value" }
 *   { job_id: "value" }
 *   { plan_type: "value" }
 */
export async function resolveAccess(payload) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ resolveAccess: No authenticated user');
      return { ok: false, error: 'User not authenticated' };
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ resolveAccess: Could not get Firebase ID token');
      return { ok: false, error: 'Authentication failed' };
    }

    if (!payload || typeof payload !== 'object') {
      console.error('❌ resolveAccess: Invalid payload');
      return { ok: false, error: 'Invalid request' };
    }

    console.log('📤 Calling POST /access/resolve with:', payload);

    const response = await fetch(`${API_BASE}/access/resolve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();
    console.log('Backend /access/resolve response:', data);

    if (!response.ok) {
      const errorMsg = data.message || data.error_code || 'Access resolution failed';
      console.error(`❌ Backend /access/resolve failed: ${response.status} - ${errorMsg}`);
      return {
        ok: false,
        error: errorMsg
      };
    }

    // Successful response includes redirect_to field
    if (data.redirect_to) {
      console.log('✅ Access resolved, redirect to:', data.redirect_to);
      return {
        ok: true,
        redirect_to: data.redirect_to,
        entitlement_id: data.entitlement_id || null,
        payment_intent_id: data.payment_intent_id || null
      };
    } else {
      console.error('❌ Missing redirect_to in response:', data);
      return {
        ok: false,
        error: 'Invalid response from server'
      };
    }
  } catch (error) {
    console.error('❌ Failed to resolve access:', error);
    return {
      ok: false,
      error: error.message || 'Network error'
    };
  }
}

/**
 * Create Razorpay payment order (New payment flow)
 * 
 * Calls backend /payment/create-order to:
 * 1. Validate plan type
 * 2. Get pricing amount
 * 3. Create Razorpay order
 * 4. Return order details for checkout
 * 
 * NOTE: This is DIFFERENT from the legacy createPaymentOrder above.
 * Use this for real Razorpay payments.
 * 
 * @param {string} planType - Plan type (SINGLE_INTERVIEW | FIVE_INTERVIEWS | TEN_INTERVIEWS | MONTHLY_SUBSCRIPTION)
 * @returns {Promise<Object|null>} - Order details or null on error
 * 
 * Response format:
 * {
 *   status: "success",
 *   order_id: string,
 *   amount: number (paise),
 *   currency: "INR",
 *   razorpay_key_id: string,
 *   plan_type: string
 * }
 */
export async function createRazorpayOrder(planType, discountCode = null, trialOffer = false) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ createRazorpayOrder: No authenticated user');
      return { status: 'error', message: 'User not authenticated' };
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ createRazorpayOrder: Could not get Firebase ID token');
      return { status: 'error', message: 'Authentication failed' };
    }

    if (!planType) {
      console.error('❌ createRazorpayOrder: planType is required');
      return { status: 'error', message: 'Plan type is required' };
    }

    const requestBody = { plan_type: planType };
    if (discountCode) {
      requestBody.discount_code = discountCode;
    }
    if (trialOffer) {
      requestBody.trial_offer = true;
    }

    console.log('📤 Calling POST /payment/create-order:', requestBody);

    const response = await fetch(`${API_BASE}/payment/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    console.log('📥 Backend /payment/create-order response:', data);

    if (!response.ok) {
      const errorMsg = data.message || data.error_code || 'Payment order creation failed';
      console.error(`❌ Backend /payment/create-order failed: ${response.status} - ${errorMsg}`);
      return {
        status: 'error',
        error_code: data.error_code,
        message: errorMsg
      };
    }

    // Validate successful response
    if (data.status === 'success') {
      // Check for either regular Razorpay order OR free activation
      if ((data.order_id && data.razorpay_key_id) || data.free_activation) {
        if (data.free_activation) {
          console.log('✅ Free activation:', data.payment_reference);
        } else {
          console.log('✅ Razorpay order created:', data.order_id);
        }
        return data;
      }
    }
    
    console.error('❌ Unexpected response format:', data);
    return {
      status: 'error',
      message: 'Invalid response from server'
    };
  } catch (error) {
    console.error('❌ Failed to create Razorpay order:', error);
    return {
      status: 'error',
      message: error.message || 'Network error'
    };
  }
}

/**
 * Validate discount coupon and get discounted price
 * 
 * Calls backend /payment/validate-coupon to:
 * 1. Validate coupon code against Firestore
 * 2. Check if coupon is valid for selected plan
 * 3. Calculate discounted price
 * 4. Return discount details for display
 * 
 * @param {string} couponCode - Coupon code to validate
 * @param {string} planType - Plan type (e.g., "SINGLE_INTERVIEW")
 * @returns {Promise<Object>} - Validation result with discount details
 * 
 * Success response:
 * {
 *   status: "success",
 *   valid: true,
 *   coupon_code: "SAVE50",
 *   original_price: 19900,
 *   final_price: 9950,
 *   discount_amount: 9950,
 *   discount_percentage: 50
 * }
 * 
 * Error response:
 * {
 *   status: "error",
 *   valid: false,
 *   message: "This coupon has expired"
 * }
 */
export async function validateCoupon(couponCode, planType) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ validateCoupon: No authenticated user');
      return { 
        status: 'error', 
        valid: false,
        message: 'User not authenticated' 
      };
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ validateCoupon: Could not get Firebase ID token');
      return { 
        status: 'error', 
        valid: false,
        message: 'Authentication failed' 
      };
    }

    if (!couponCode || !planType) {
      console.error('❌ validateCoupon: couponCode and planType are required');
      return { 
        status: 'error', 
        valid: false,
        message: 'Coupon code and plan type are required' 
      };
    }

    console.log(`📤 Calling POST /payment/validate-coupon: ${couponCode} for ${planType}`);

    const response = await fetch(`${API_BASE}/payment/validate-coupon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        coupon_code: couponCode,
        plan_type: planType 
      })
    });

    const data = await response.json();
    console.log('📥 Backend /payment/validate-coupon response:', data);

    if (!response.ok) {
      const errorMsg = data.message || 'Coupon validation failed';
      console.error(`❌ Backend /payment/validate-coupon failed: ${response.status} - ${errorMsg}`);
      return {
        status: 'error',
        valid: false,
        message: errorMsg
      };
    }

    // Validate successful response
    if (data.status === 'success' && data.valid === true) {
      console.log('✅ Coupon validated successfully:', data.coupon_code);
      return data;
    } else {
      console.error('❌ Invalid coupon:', data.message);
      return {
        status: 'error',
        valid: false,
        message: data.message || 'Invalid coupon code'
      };
    }
  } catch (error) {
    console.error('❌ Failed to validate coupon:', error);
    return {
      status: 'error',
      valid: false,
      message: error.message || 'Network error'
    };
  }
}


/**
 * Verify Razorpay payment after successful checkout
 * 
 * Calls backend /payment/verify to:
 * 1. Verify payment signature (HMAC SHA256)
 * 2. Store payment record in Firestore
 * 3. Activate entitlement
 * 4. Return redirect information
 * 
 * @param {Object} paymentData - Payment verification data
 * @param {string} paymentData.razorpay_order_id - Razorpay order ID
 * @param {string} paymentData.razorpay_payment_id - Razorpay payment ID
 * @param {string} paymentData.razorpay_signature - Razorpay signature
 * @param {string} paymentData.plan_type - Plan type purchased
 * @returns {Promise<Object|null>} - Verification result or null on error
 * 
 * Response format:
 * {
 *   success: true,
 *   redirect_to: "INTERVIEWEE_DASHBOARD",
 *   entitlement_id: string,
 *   subscription_id: string,
 *   payment_id: string,
 *   valid_from: string (ISO timestamp),
 *   valid_till: string (ISO timestamp)
 * }
 */
export async function verifyPayment(paymentData) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.error('❌ verifyPayment: No authenticated user');
      return { success: false, message: 'User not authenticated' };
    }

    const token = await user.getIdToken();
    if (!token) {
      console.error('❌ verifyPayment: Could not get Firebase ID token');
      return { success: false, message: 'Authentication failed' };
    }

    if (!paymentData.razorpay_order_id || !paymentData.razorpay_payment_id || !paymentData.razorpay_signature || !paymentData.plan_type) {
      console.error('❌ verifyPayment: Missing required fields');
      return { success: false, message: 'Missing payment verification data' };
    }

    console.log('📤 Calling POST /payment/verify with:', {
      order_id: paymentData.razorpay_order_id,
      payment_id: paymentData.razorpay_payment_id,
      plan_type: paymentData.plan_type
    });

    const response = await fetch(`${API_BASE}/payment/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(paymentData)
    });

    const data = await response.json();
    console.log('📥 Backend /payment/verify response:', data);

    if (!response.ok) {
      const errorMsg = data.message || data.error_code || 'Payment verification failed';
      console.error(`❌ Backend /payment/verify failed: ${response.status} - ${errorMsg}`);
      return {
        success: false,
        error_code: data.error_code,
        message: errorMsg
      };
    }

    // Validate successful response
    if (data.success && data.entitlement_id) {
      console.log('✅ Payment verified, entitlement activated:', data.entitlement_id);
      return data;
    } else {
      console.error('❌ Unexpected response format:', data);
      return {
        success: false,
        message: 'Invalid response from server'
      };
    }
  } catch (error) {
    console.error('❌ Failed to verify payment:', error);
    return {
      success: false,
      message: error.message || 'Network error'
    };
  }
}

async function parseCodingResponse(response, fallbackMessage) {
  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    data = {};
  }

  if (!response.ok) {
    throw new Error(data.message || data.error || data.error_code || fallbackMessage);
  }

  return data;
}

export async function getCodingPatterns() {
  const response = await authenticatedFetch(`${API_BASE}/coding/patterns`);
  return parseCodingResponse(response, 'Unable to load coding patterns');
}

export async function getCodingPattern(patternId) {
  const response = await authenticatedFetch(`${API_BASE}/coding/pattern/${encodeURIComponent(patternId)}`);
  return parseCodingResponse(response, 'Unable to load coding pattern');
}

export async function getCodingQuestions(pattern) {
  const response = await authenticatedFetch(`${API_BASE}/coding/problems/${encodeURIComponent(pattern)}`);
  return parseCodingResponse(response, 'Unable to load coding questions');
}

export async function getCodingQuestion(problemId) {
  const response = await authenticatedFetch(`${API_BASE}/coding/problem/${encodeURIComponent(problemId)}`);
  return parseCodingResponse(response, 'Unable to load coding question');
}

export async function runCode({ problemId, language, sourceCode }) {
  const response = await authenticatedFetch(`${API_BASE}/coding/run`, {
    method: 'POST',
    body: JSON.stringify({
      problem_id: problemId,
      language,
      source_code: sourceCode,
    }),
  });
  return parseCodingResponse(response, 'Unable to run code');
}

export async function submitCode({ problemId, language, sourceCode, idempotencyKey }) {
  const response = await authenticatedFetch(`${API_BASE}/coding/submit`, {
    method: 'POST',
    body: JSON.stringify({
      problem_id: problemId,
      language,
      source_code: sourceCode,
      idempotency_key: idempotencyKey,
    }),
  });
  return parseCodingResponse(response, 'Unable to submit code');
}

export async function getCodingJob(jobId, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 10000);
  try {
    const response = await authenticatedFetch(`${API_BASE}/coding/jobs/${encodeURIComponent(jobId)}`, { signal: controller.signal });
    return await parseCodingResponse(response, 'Unable to check grading status');
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

export async function getCodingProgress() {
  const response = await authenticatedFetch(`${API_BASE}/coding/progress`);
  return parseCodingResponse(response, 'Unable to load coding progress');
}

export async function saveCodingDraft({ problemId, language, sourceCode }) {
  const response = await authenticatedFetch(`${API_BASE}/coding/draft`, {
    method: 'POST',
    body: JSON.stringify({
      problem_id: problemId,
      language,
      source_code: sourceCode,
    }),
  });
  return parseCodingResponse(response, 'Unable to save draft');
}

export async function bookmarkCodingProblem({ problemId, bookmarked = true }) {
  const response = await authenticatedFetch(`${API_BASE}/coding/bookmark`, {
    method: 'POST',
    body: JSON.stringify({
      problem_id: problemId,
      bookmarked,
    }),
  });
  return parseCodingResponse(response, 'Unable to update bookmark');
}

/**
 * Submit a landing-page "Contact us" enquiry (public, no auth).
 * Resolves on success; throws an Error with a user-facing message otherwise.
 */
export async function submitContactEnquiry(enquiry) {
  let response;
  try {
    response = await fetch(`${API_BASE}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enquiry),
    });
  } catch {
    throw new Error('Network error. Please check your connection and try again.');
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Something went wrong. Please try again.');
  }
  return response.json();
}
